import { fetchJson } from './client';
import { StoreApiError } from './types';

export interface AppDataResponse {
  data: Record<string, unknown> | null;
  etag: string;
}

export interface PutAppDataInput {
  productId: string;
  data: Record<string, unknown>;
  ifMatch?: string;
}

function normalizeEtag(raw: unknown): string {
  if (typeof raw !== 'string') {
    throw new StoreApiError('invalid-response', 'App data server returned an invalid etag.');
  }
  const t = raw.trim();
  if (!/^\d+$/.test(t)) {
    throw new StoreApiError('invalid-response', 'App data server returned an invalid etag.');
  }
  return t;
}

function normalizeData(raw: unknown): Record<string, unknown> | null {
  if (raw === null) return null;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new StoreApiError('invalid-response', 'App data server returned an unexpected data shape.');
  }
  return raw as Record<string, unknown>;
}

export async function fetchAppData(productId: string): Promise<AppDataResponse> {
  const data = await fetchJson<{ data?: unknown; etag?: unknown }>({
    method: 'GET',
    relativePath: `/appData?productId=${encodeURIComponent(productId)}`,
    errorContext: 'Fetch app data',
  });
  return {
    data: normalizeData(data?.data ?? null),
    etag: normalizeEtag(data?.etag),
  };
}

export async function putAppData(input: PutAppDataInput): Promise<{ etag: string }> {
  const body: Record<string, unknown> = {
    productId: input.productId,
    data: input.data,
  };
  if (input.ifMatch !== undefined) {
    body.ifMatch = input.ifMatch;
  }
  const data = await fetchJson<{ etag?: unknown }>({
    method: 'PUT',
    relativePath: '/appData',
    body,
    errorContext: 'Put app data',
  });
  return { etag: normalizeEtag(data?.etag) };
}

export async function updateAppDataWithRetry(
  productId: string,
  mutator: (current: Record<string, unknown>) => Record<string, unknown>,
  maxAttempts = 3,
): Promise<AppDataResponse> {
  for (let i = 0; i < maxAttempts; i += 1) {
    const current = await fetchAppData(productId);
    const nextData = mutator(current.data ?? {});
    try {
      const write = await putAppData({
        productId,
        data: nextData,
        ifMatch: current.etag,
      });
      return { data: nextData, etag: write.etag };
    } catch (err: unknown) {
      if (err instanceof StoreApiError && err.status === 409) {
        continue;
      }
      throw err;
    }
  }
  throw new StoreApiError('server', `Failed to update app data after ${maxAttempts} attempts.`);
}
